import React, { useEffect, useState } from 'react'
import { styled, ThemeProvider } from '@mui/material/styles'
import './App.css'
import 'primereact/resources/themes/nova/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import Routes from './Routes'
import { signOut, fetchAuthSession } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import config from './config'
import NotificationBar from './NotificationBar'
import AccountCircle from '@mui/icons-material/AccountCircle'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Logo from './components/Logo'
import withMediaQuery from './utils/withMediaQuery'
import { createTheme, ScopedCssBaseline, useTheme } from '@mui/material'
import Brightness4Icon from '@mui/icons-material/Brightness4'
import Brightness7Icon from '@mui/icons-material/Brightness7'

const PREFIX = 'App'

const classes = {
  root: `${PREFIX}-root`,
  appBarFull: `${PREFIX}-appBarFull`,
  appBarMobile: `${PREFIX}-appBarMobile`,
  appBarFullScreen: `${PREFIX}-appBarFullScreen`,
  appBarButton: `${PREFIX}-appBarButton`,
  drawer: `${PREFIX}-drawer`,
  drawerPaper: `${PREFIX}-drawerPaper`,
  toolbar: `${PREFIX}-toolbar`,
  content: `${PREFIX}-content`,
  contentFullScreen: `${PREFIX}-contentFullScreen`,
}

const Root = styled('div')(({ theme }) => ({
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1) rotate(0deg)' },
    '50%': { transform: 'scale(1.1) rotate(180deg)' },
  },
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',

  [`& .${classes.appBarFull}`]: {
    width: '100%',
    background: theme.palette.mode === 'dark' ? 'rgba(10, 10, 10, 0.85)' : theme.palette.background.paper,
    backdropFilter: 'blur(12px)',
    borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(64, 64, 64, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
    boxShadow: theme.palette.mode === 'dark' ? '0 4px 24px rgba(0, 0, 0, 0.6)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  },

  [`& .${classes.appBarMobile}`]: {
    width: '100%',
    background: theme.palette.mode === 'dark' ? 'rgba(10, 10, 10, 0.85)' : theme.palette.background.paper,
    backdropFilter: 'blur(12px)',
    borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(64, 64, 64, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
    boxShadow: theme.palette.mode === 'dark' ? '0 4px 24px rgba(0, 0, 0, 0.6)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
  },

  [`& .${classes.appBarFullScreen}`]: {
    width: `100%`,
    marginLeft: drawerWidth,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    boxShadow: 'none',
  },

  [`& .${classes.appBarButton}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.drawer}`]: {
    width: drawerWidth,
    flexShrink: 0,
  },

  [`& .${classes.drawerPaper}`]: {
    width: drawerWidth,
  },

  [`& .${classes.toolbar}`]: theme.mixins.toolbar,

  [`& .${classes.content}`]: {
    flexGrow: 1,
    marginTop: '70px',
    background:
      theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)'
        : theme.palette.background.default,
    padding: theme.spacing(3),
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: '56px',
      padding: theme.spacing(1),
    },
    '&::before':
      theme.palette.mode === 'dark'
        ? {
            content: '""',
            position: 'fixed',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(64, 64, 64, 0.08) 0%, transparent 70%)',
            animation: 'pulse 20s ease-in-out infinite',
            pointerEvents: 'none',
            zIndex: 0,
          }
        : {},
  },

  [`& .${classes.contentFullScreen}`]: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
  },
}))

const ColorModeContext = React.createContext({ toggleColorMode: () => {} })

const drawerWidth = 251

const NewApp = (props: { isMobile?: boolean }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const colorMode = React.useContext(ColorModeContext)

  const [appState, setAppState] = useState({
    isAuthenticated: false,
    showNotification: true,
    isAuthenticating: true,
    userPermissions: new Array(),
    userPermittedPaths: new Array(),
    userDefaultPath: '',
    left: false,
    anchorEl: null as HTMLElement | null,
    isFullScreen: true,
    mouseX: null,
    mouseY: null,
  })

  const [isAuthenticating, setIsAuthenticating] = useState(true)

  useEffect(() => {
    const handleAuth = (data: any) => {
      const { payload } = data
      console.log('Auth event!', data)
      if (payload.event === 'signIn' || payload.event === 'cognitoHostedUI') {
        signIn()
      }
    }

    Hub.listen('auth', handleAuth)

    const loadUserSession = async () => {
      try {
        const session = await fetchAuthSession()
        if (session.tokens) {
          await signIn()
        }
      } catch (error) {
        console.error('Error during session load:', error)
      }
    }

    loadUserSession().then(() => {
      setIsAuthenticating(false)
    })
  }, [])

  const signIn = async () => {
    const session = await fetchAuthSession()
    const credentials = session.credentials

    userHasAuthenticated(true, ['*'], ['*'], '/')

    if (config.setting.STAGE !== 'test' && config.setting.STAGE !== 'prod') {
      console.log({
        accessKeyId: credentials?.accessKeyId,
        secretAccessKey: credentials?.secretAccessKey,
        sessionToken: credentials?.sessionToken,
        service: 'execute-api',
        region: 'us-west-2',
      })
    }
  }

  const userHasAuthenticated = (
    authenticated: boolean,
    userPermissions: any[],
    userPermittedPaths: any[],
    userDefaultPath: string
  ) => {
    setAppState({
      ...appState,
      isAuthenticated: authenticated,
      userPermissions: [...userPermissions],
      userPermittedPaths: [...userPermittedPaths],
      userDefaultPath: userDefaultPath,
    })
  }

  const setUserPermissions = (permissions: any) => {
    setAppState({ ...appState, userPermissions: [...permissions, ...appState.userPermissions] })
  }

  const setUserPermittedPaths = (path: any) => {
    setAppState({ ...appState, userPermittedPaths: path })
  }

  const setUserDefaultPath = (path: any) => {
    setAppState({ ...appState, userDefaultPath: path })
  }

  const handleProfile = async () => {
    setAppState({ ...appState, anchorEl: null })
    navigate('/profile')
  }

  const handleLogout = async () => {
    try {
      await signOut()
      // Note: stopSession is no longer available in Amplify v6
      // Analytics are automatically managed
    } catch (error) {
      console.error('Error signing out:', error)
    }

    // Update state to unauthenticated
    setAppState({
      ...appState,
      isAuthenticated: false,
      anchorEl: null,
      userPermissions: [],
      userPermittedPaths: [],
      userDefaultPath: '',
    })

    // Navigate to login page
    navigate('/login')
  }

  const toggleIsFullScreen = () => {
    const { isFullScreen } = appState
    setAppState({
      ...appState,
      isFullScreen: !isFullScreen,
      anchorEl: null,
      mouseX: null,
      mouseY: null,
    })
  }

  const childProps = {
    isAuthenticated: appState.isAuthenticated,
    userHasAuthenticated: userHasAuthenticated,
    setUserPermissions: setUserPermissions,
    setUserPermittedPaths: setUserPermittedPaths,
    setUserDefaultPath: setUserDefaultPath,
    userPermissions: appState.userPermissions,
    userPermittedPaths: appState.userPermittedPaths,
    userDefaultPath: appState.userDefaultPath,
  }

  const open = Boolean(appState.anchorEl)
  const { isMobile } = props
  const isLoginPage = location.pathname === '/login'

  return (
    !isAuthenticating && (
      <Root className={classes.root}>
        {!isLoginPage && (
          <AppBar position="fixed" className={isMobile ? classes.appBarMobile : classes.appBarFull}>
            <Toolbar sx={{ placeContent: 'space-between', minHeight: { xs: '56px', sm: '70px' } }}>
              <Typography variant="h6" color="inherit">
                <Logo />
              </Typography>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconButton
                  onClick={colorMode.toggleColorMode}
                  color="inherit"
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {theme.palette.mode == 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
                {appState.isAuthenticated ? (
                  <>
                    <IconButton
                      aria-haspopup="true"
                      onClick={(event) => setAppState({ ...appState, anchorEl: event.currentTarget })}
                      color="inherit"
                      size="large"
                      sx={{
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <AccountCircle />
                    </IconButton>
                    <Menu
                      id="menu-appbar"
                      anchorEl={appState.anchorEl}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            width: 200,
                            mt: 1.5,
                            background:
                              theme.palette.mode === 'dark' ? 'rgba(23, 23, 23, 0.95)' : theme.palette.background.paper,
                            backdropFilter: 'blur(20px)',
                            border:
                              theme.palette.mode === 'dark'
                                ? '1px solid rgba(64, 64, 64, 0.3)'
                                : '1px solid rgba(0, 0, 0, 0.12)',
                            boxShadow:
                              theme.palette.mode === 'dark'
                                ? '0 8px 32px rgba(0, 0, 0, 0.6)'
                                : '0 4px 16px rgba(0, 0, 0, 0.1)',
                          },
                        },
                      }}
                      open={open}
                      onClose={() => setAppState({ ...appState, anchorEl: null, mouseX: null, mouseY: null })}
                    >
                      <MenuItem onClick={toggleIsFullScreen}>Enter Full Screen</MenuItem>
                      <Divider />
                      <MenuItem onClick={handleProfile}>Profile</MenuItem>
                      <MenuItem onClick={handleLogout}>Logout</MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Button
                    color="inherit"
                    component={Link}
                    to="/login"
                    sx={{
                      borderRadius: '8px',
                      padding: '8px 20px',
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark' ? 'rgba(64, 64, 64, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    Login
                  </Button>
                )}
              </div>
            </Toolbar>
          </AppBar>
        )}

        <main className={classes.content} style={isLoginPage ? { padding: 0, backgroundColor: 'transparent', marginTop: 0 } : {}}>
          <div style={{ marginLeft: 0, position: 'relative', zIndex: 1 }}>
            <Routes childProps={childProps} />
          </div>
        </main>
        <NotificationBar />
      </Root>
    )
  )
}

const AppPage = NewApp

const ToggleColorMode = () => {
  // const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  let defaultColorScheme = localStorage.getItem('colorScheme')
  let prefersDarkMode = false

  if (defaultColorScheme) {
    if (defaultColorScheme == 'dark') {
      prefersDarkMode = true
    }
  }

  const [mode, setMode] = React.useState<'light' | 'dark'>(prefersDarkMode ? 'dark' : 'light')

  // Update data attribute on body for theme-aware styling
  React.useEffect(() => {
    document.body.setAttribute('data-theme', mode)
  }, [mode])
  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          if (prevMode == 'light') {
            localStorage.setItem('colorScheme', 'dark')
          } else {
            localStorage.removeItem('colorScheme')
          }
          return prevMode === 'light' ? 'dark' : 'light'
        })
      },
    }),
    []
  )

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            light: '#757ce8',
            main: '#fff',
            dark: '#e7e7e7',
            contrastText: '#000000de',
          },
          secondary: {
            light: '#333',
            main: '#666',
            dark: '#999',
            contrastText: '#ccc',
          },
          text: {
            ...(mode === 'light'
              ? {}
              : {
                  primary: '#dedede',
                  secondary: '#dedede',
                }),
          },
        },
        components: {
          MuiListItem: {
            styleOverrides: {
              button: {
                cursor: 'pointer',
                '&:active': {
                  color: '#fff',
                },
                selected: {
                  color: 'red !important',
                },
              },
            },
          },
          MuiListItemIcon: {
            styleOverrides: {
              root: {
                cursor: 'pointer',
                '&:active': {
                  color: 'blue',
                },
                marginRight: 0,
                color: '#b4b4b4',
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                ...(mode === 'dark'
                  ? {
                      backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
                    }
                  : {}),
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              ...(mode === 'dark'
                ? {
                    root: {
                      backgroundColor: 'black',
                    },
                  }
                : {
                    root: {
                      backgroundColor: 'white',
                    },
                  }),
            },
          },
        },
      }),
    [mode]
  )

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <ScopedCssBaseline sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <AppPage />
        </ScopedCssBaseline>
      </ThemeProvider>
    </ColorModeContext.Provider>
  )
}

//export default (AppPage);
export default withMediaQuery()(ToggleColorMode)
